import { z } from "zod";
import {
  legacyMerchTerritorySchema,
  merchTerritorySchema,
} from "./merch-territory";
import {
  artistLinkPageArtistSchema,
  artistLinkPageAccessSchema,
  artistLinkPageMerchSchema,
  artistLinkPageSongItemSchema,
  artistLinkPageSocialIcons,
  artistLinkPageSectionSchema,
  artistLinkPageMailingListSectionSchema,
  artistLinkPageSlugSchema,
  artistLinkPageThemeSchema,
} from "./artist-link-page";

const httpUrlSchema = z
  .string()
  .url()
  .refine((value) => /^https?:\/\//i.test(value), {
    message: "URL must use http or https",
  });

/**
 * One option of an item sold elsewhere (a size, say), with its own link in
 * the merchant's store. A sold-out option is shown and not linked.
 */
export const artistSiteExternalMerchOptionSchema = z.object({
  id: z.string().min(1),
  label: z.string().min(1).max(40),
  url: httpUrlSchema.nullable(),
  soldOut: z.boolean(),
  /** This option's own price, as written; the item's when null. */
  priceLabel: z.string().max(40).nullable(),
});

/**
 * A merch item sold elsewhere: typed by hand on the site, it lives in a
 * merchant's own store (Music Glue, say), and its card sends the visitor
 * there: to the item's own link, or to the link of the option chosen.
 *
 * `url` is null only for an item with options, which builds that do not
 * draw options never receive (`?merchOptions=1`, see the platform API's
 * merch compatibility); `options` defaults so older payloads parse.
 */
export const artistSiteExternalMerchSchema = z.object({
  type: z.literal("external"),
  id: z.string().min(1),
  title: z.string().min(1).max(120),
  priceLabel: z.string().max(40).nullable(),
  imageUrl: httpUrlSchema.nullable(),
  merchant: z.string().min(1).max(60),
  url: httpUrlSchema.nullable(),
  options: z.array(artistSiteExternalMerchOptionSchema).default([]),
});

/** One thing a Merch section sells: 1-tap merch, or an item sold elsewhere. */
export const artistSiteMerchItemSchema = z.discriminatedUnion("type", [
  artistLinkPageMerchSchema,
  artistSiteExternalMerchSchema,
]);

export const artistSiteButtonSchema = z.object({
  label: z.string().min(1).max(40),
  url: httpUrlSchema,
});

/** A labelled link out of the site: a menu entry or a footer link. */
export const artistSiteLinkSchema = z.object({
  id: z.string().min(1),
  label: z.string().min(1).max(40),
  url: httpUrlSchema,
});

/** The Email icon may open a `mailto:` address; every other icon a web page. */
export const artistSiteSocialLinkSchema = z.object({
  id: z.string().min(1),
  icon: z.enum(artistLinkPageSocialIcons),
  url: z.union([
    httpUrlSchema,
    z.string().regex(/^mailto:[^\s@/?#]+@[^\s@/?#]+\.[^\s@/?#]+$/i),
  ]),
});

/**
 * One of a site's own typefaces: the family it is drawn under and where its
 * faces come from, a web font's stylesheet or a font file. The family is a
 * listed web font's name or one of Lissen's for a font file, never text the
 * artist typed, so it is safe to write into a stylesheet.
 */
export const artistSiteFontSchema = z.object({
  family: z.string().regex(/^[A-Za-z0-9][A-Za-z0-9 ]{0,79}$/),
  /** Drawn while the font loads, or if it never does. */
  fallback: z.enum(["sans-serif", "serif", "monospace", "cursive"]),
  stylesheetUrl: httpUrlSchema.nullable(),
  fileUrl: httpUrlSchema.nullable(),
  /** The weight headings set in it are drawn at. */
  weight: z.number().int().min(100).max(900),
});

/**
 * The Link in Bio page theme plus how the site's headings are set, its own
 * typefaces and how its buttons are drawn.
 */
export const artistSiteThemeSchema = artistLinkPageThemeSchema.extend({
  headingFont: z
    .enum(["default", "serif", "mono", "condensed"])
    .default("default"),
  uppercaseHeadings: z.boolean().default(false),
  /** In place of `headingFont` for headings, and of the standard body text. */
  fonts: z
    .object({
      heading: artistSiteFontSchema.nullable(),
      body: artistSiteFontSchema.nullable(),
    })
    .default({ heading: null, body: null }),
  buttonStyle: z.enum(["filled", "outline"]).default("filled"),
});

/**
 * What every page of a site shares: name, look, logo, menu, socials, footer.
 *
 * Everything added after the first release of the payload has a default, so a
 * client that is ahead of the service (or reads an older cached response)
 * still parses it.
 */
export const artistSiteFrameSchema = z.object({
  title: z.string().min(1),
  theme: artistSiteThemeSchema,
  logoUrl: httpUrlSchema.nullable().default(null),
  /** Links out of the site, shown in the menu after its pages. */
  menuLinks: z.array(artistSiteLinkSchema).default([]),
  /**
   * Where the menu's entries and logo sit, whether it stays at the top on
   * scroll, its own background (the page's when null) and text colour
   * (automatic when null), and whether it shows the social icons.
   */
  menu: z
    .object({
      align: z.enum(["start", "center", "end"]).default("end"),
      sticky: z.boolean().default(true),
      background: z.string().nullable().default(null),
      textColor: z.string().nullable().default(null),
      logo: z.enum(["start", "center"]).default("start"),
      socials: z.enum(["menu", "footer"]).default("menu"),
    })
    .default({
      align: "end",
      sticky: true,
      background: null,
      textColor: null,
      logo: "start",
      socials: "menu",
    }),
  socialLinks: z.array(artistSiteSocialLinkSchema).default([]),
  /** Its own background (the page's when null) and text colour (automatic when null). */
  footer: z
    .object({
      text: z.string().max(240).nullable(),
      links: z.array(artistSiteLinkSchema),
      background: z.string().nullable().default(null),
      textColor: z.string().nullable().default(null),
    })
    .default({ text: null, links: [], background: null, textColor: null }),
});

/**
 * One of a hero's buttons: a link (label and address), or a release picked
 * from the artist's Lissen catalogue (`catalog`, resolved as a Music
 * section's pick is), which plays in place or opens where else to hear it.
 */
export const artistSiteHeroButtonSchema = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("link"),
    id: z.string().min(1),
    label: z.string().min(1).max(40),
    url: httpUrlSchema,
  }),
  z.object({
    kind: z.literal("release"),
    id: z.string().min(1),
    label: z.string().min(1).max(40),
    /** The release's title, which names the button with its label. */
    title: z.string().min(1).max(120),
    catalog: artistLinkPageSongItemSchema,
  }),
  /**
   * One of the artist's Lissen events, opened over the site as a picked
   * live date's Tickets button opens it. `label` is the site's own words,
   * which a price or a held ticket replaces; "Tickets" when null.
   */
  z.object({
    kind: z.literal("event"),
    id: z.string().min(1),
    label: z.string().min(1).max(40).nullable().default(null),
    access: artistLinkPageAccessSchema,
  }),
]);

export const artistSiteHeroSectionSchema = z.object({
  id: z.string().min(1),
  type: z.literal("hero"),
  title: z.string().min(1).max(120),
  subtitle: z.string().max(240).nullable(),
  imageUrl: httpUrlSchema.nullable(),
  /** `full` fills the visitor's first screen. */
  height: z.enum(["banner", "full"]).default("banner"),
  /** A YouTube video played silently behind the hero. */
  videoUrl: httpUrlSchema.nullable().default(null),
  /** Shown in place of the title's text. */
  logoUrl: httpUrlSchema.nullable().default(null),
  /** False when the title only names the hero and is not drawn. */
  showTitle: z.boolean().default(true),
  /** How much the picture or video is darkened behind the words. */
  dim: z.enum(["none", "light", "medium", "strong"]).default("medium"),
  /** What shows until a background video plays, without an image of its own. */
  videoStill: z.enum(["thumbnail", "none"]).default("thumbnail"),
  /**
   * The hero's first link button, for a page built before heroes had several
   * buttons. A current page draws `buttons` (`artistSiteHeroButtons`).
   */
  button: artistSiteButtonSchema.nullable().default(null),
  /**
   * The hero's buttons in order, the first drawn filled and the others
   * outlined. Defaulted so a payload from before heroes had several still
   * parses; `artistSiteHeroButtons` then reads its one `button`.
   */
  buttons: z.array(artistSiteHeroButtonSchema).default([]),
});

/**
 * A hero's buttons as a page draws them: its `buttons`, or the one `button`
 * of a payload resolved before heroes had several.
 */
export function artistSiteHeroButtons(hero: {
  id: string;
  button: z.infer<typeof artistSiteButtonSchema> | null;
  buttons?: ArtistSiteHeroButton[];
}): ArtistSiteHeroButton[] {
  if (hero.buttons && hero.buttons.length > 0) return hero.buttons;
  return hero.button
    ? [{ kind: "link", id: `${hero.id}-button`, ...hero.button }]
    : [];
}

const artistSiteImageSchema = z.object({
  id: z.string().min(1),
  imageUrl: httpUrlSchema,
  alt: z.string().max(200).nullable(),
});

const colourSchema = z.string().regex(/^#[0-9a-f]{6}$/i);

/**
 * What a section sets about its own look, over the site's: how its heading
 * is set, and its colours. Absent where the section follows the site.
 */
const artistSiteSectionLookShape = {
  heading: z
    .object({
      font: z.enum(["default", "serif", "mono", "condensed"]).optional(),
      color: colourSchema.optional(),
      align: z.enum(["start", "center", "end"]).optional(),
      size: z.enum(["small", "medium", "large"]).optional(),
      uppercase: z.boolean().optional(),
    })
    .optional(),
  style: z
    .object({
      background: colourSchema.optional(),
      textColor: colourSchema.optional(),
      buttonBackground: colourSchema.optional(),
      buttonTextColor: colourSchema.optional(),
      buttonShape: z.enum(["rounded", "pill", "square"]).optional(),
      buttonStyle: z.enum(["filled", "outline"]).optional(),
    })
    .optional(),
} as const;

export const artistSiteCarouselSectionSchema = z.object({
  id: z.string().min(1),
  type: z.literal("carousel"),
  title: z.string().max(120).nullable(),
  ...artistSiteSectionLookShape,
  /** Moves to its next slide by itself. */
  autoplay: z.boolean().optional(),
  items: z.array(
    artistSiteImageSchema.extend({ url: httpUrlSchema.nullable() }),
  ),
});

/** How many cards a grid shows side by side on a wide screen. */
const artistSiteGridColumnsSchema = z.union([
  z.literal(2),
  z.literal(3),
  z.literal(4),
  z.literal(5),
]);

export const artistSiteGallerySectionSchema = z.object({
  id: z.string().min(1),
  type: z.literal("gallery"),
  title: z.string().max(120).nullable(),
  ...artistSiteSectionLookShape,
  /** A grid, or one row that scrolls sideways. A grid when absent. */
  layout: z.enum(["grid", "carousel"]).optional(),
  /** The pictures side by side on a wide screen; four when absent. */
  columns: artistSiteGridColumnsSchema.optional(),
  /** A carousel that moves to its next picture by itself. */
  autoplay: z.boolean().optional(),
  items: z.array(artistSiteImageSchema),
});

export const artistSiteCtaSectionSchema = z.object({
  id: z.string().min(1),
  type: z.literal("cta"),
  title: z.string().min(1).max(120),
  ...artistSiteSectionLookShape,
  body: z.string().max(400).nullable(),
  button: artistSiteButtonSchema,
});

export const artistSiteTextSectionSchema = z.object({
  id: z.string().min(1),
  type: z.literal("text"),
  title: z.string().max(120).nullable(),
  ...artistSiteSectionLookShape,
  body: z.string().min(1).max(4000),
});

/** One block of a Columns section's column. */
export const artistSiteColumnBlockSchema = z.discriminatedUnion("type", [
  z.object({
    id: z.string().min(1),
    type: z.literal("heading"),
    text: z.string().min(1).max(120),
  }),
  z.object({
    id: z.string().min(1),
    type: z.literal("text"),
    body: z.string().min(1).max(4000),
  }),
  z.object({
    id: z.string().min(1),
    type: z.literal("image"),
    imageUrl: httpUrlSchema,
    alt: z.string().max(200).nullable(),
    /** Where a click on the picture leads. */
    url: httpUrlSchema.nullable(),
  }),
  z.object({
    id: z.string().min(1),
    type: z.literal("button"),
    label: z.string().min(1).max(40),
    url: httpUrlSchema,
  }),
]);

/**
 * Columns side by side on a wide screen, stacked on a phone, each an ordered
 * list of blocks. Every field but the columns has a default.
 */
export const artistSiteColumnsSectionSchema = z.object({
  id: z.string().min(1),
  type: z.literal("columns"),
  title: z.string().max(120).nullable().default(null),
  ...artistSiteSectionLookShape,
  /** All equal, or the first or last twice as wide as each other one. */
  widths: z.enum(["equal", "firstWide", "lastWide"]).default("equal"),
  /** Where shorter columns sit beside the tallest. */
  verticalAlign: z.enum(["start", "center", "end"]).default("start"),
  gap: z.enum(["small", "medium", "large"]).default("medium"),
  columns: z.array(
    z.object({
      id: z.string().min(1),
      blocks: z.array(artistSiteColumnBlockSchema),
    }),
  ),
});

export const artistSiteVideoSectionSchema = z.object({
  id: z.string().min(1),
  type: z.literal("video"),
  title: z.string().max(120).nullable(),
  ...artistSiteSectionLookShape,
  /** A grid, or one row with arrows. A grid when absent. */
  layout: z.enum(["grid", "carousel"]).optional(),
  /** The videos side by side on a wide screen; two when absent. */
  columns: artistSiteGridColumnsSchema.optional(),
  /** A carousel that moves to its next video by itself. */
  autoplay: z.boolean().optional(),
  items: z.array(
    z.object({
      id: z.string().min(1),
      url: httpUrlSchema,
      title: z.string().max(120).nullable(),
    }),
  ),
});

/**
 * Where a release can be played. The streaming services the Link in Bio page
 * knows, each with its icon, or `other` for anywhere else, named by its label.
 */
export const artistSiteReleasePlatforms = [
  "spotify",
  "appleMusic",
  "deezer",
  "youtubeMusic",
  "tidal",
  "boomplay",
  "audiomack",
  "other",
] as const;

export const artistSiteReleaseLinkSchema = z.object({
  id: z.string().min(1),
  platform: z.enum(artistSiteReleasePlatforms),
  /** The link's name: the service's own, or the one given to `other`. */
  label: z.string().min(1).max(40),
  url: httpUrlSchema,
});

export const artistSiteReleasesSectionSchema = z.object({
  id: z.string().min(1),
  type: z.literal("releases"),
  title: z.string().max(120).nullable(),
  ...artistSiteSectionLookShape,
  /**
   * A grid of covers, one row that scrolls sideways, or stacked rows. A grid
   * when absent, as a site resolved before layouts existed is.
   */
  layout: z.enum(["grid", "carousel", "list"]).optional(),
  /** The cards side by side on a wide screen; three when absent. */
  columns: artistSiteGridColumnsSchema.optional(),
  /** A carousel that moves to its next card by itself. */
  autoplay: z.boolean().optional(),
  /** What opens a release's streaming links: service icons when absent. */
  linksButton: z.enum(["icons", "button"]).optional(),
  /** The words on a `button`. */
  linksButtonLabel: z.string().max(40).optional(),
  items: z.array(
    z.object({
      id: z.string().min(1),
      title: z.string().min(1).max(120),
      /** A line under the title, e.g. `Album · 2025`. */
      detail: z.string().max(80).nullable(),
      artworkUrl: httpUrlSchema.nullable(),
      links: z.array(artistSiteReleaseLinkSchema),
      /**
       * The release in Lissen's catalogue, when it was picked from it: the
       * song or album as the Link in Bio page serves one, so it plays in
       * place and carries its Lissen and streaming links. Null for a release
       * typed by hand.
       */
      catalog: artistLinkPageSongItemSchema.nullable().default(null),
    }),
  ),
});

/** Whether tickets for a live date can still be bought. */
export const artistSiteDateStatuses = ["on_sale", "sold_out"] as const;

export const artistSiteDatesSectionSchema = z.object({
  id: z.string().min(1),
  type: z.literal("dates"),
  title: z.string().max(120).nullable(),
  ...artistSiteSectionLookShape,
  /** The words on each date's ticket button; "Tickets" when absent. */
  ticketsLabel: z.string().max(40).optional(),
  /**
   * One date a row (day, venue, button side by side), or each date's lines
   * centred one under another with its button below.
   */
  layout: z.enum(["rows", "stacked"]).default("rows"),
  /** Upcoming dates, soonest first. Empty when none is left. */
  items: z.array(
    z.object({
      id: z.string().min(1),
      /** The calendar day of the show where it happens, `YYYY-MM-DD`. */
      date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
      venue: z.string().min(1).max(120),
      city: z.string().max(120).nullable(),
      status: z.enum(artistSiteDateStatuses),
      /** Where tickets are sold; null when there is nowhere to send people. */
      ticketUrl: httpUrlSchema.nullable(),
      /**
       * The Lissen event this date is, when it was picked from the artist's
       * events: its Tickets button opens the event on the site. Defaulted so
       * a payload cached before the field existed still parses.
       */
      access: artistLinkPageAccessSchema.nullable().default(null),
    }),
  ),
});

/**
 * The artist's merch as the site lays it out: the 1-tap products the section
 * picks (as a Link in Bio Merch section picks them), each served as that
 * section serves it and opened over the site, where Lissen buys it for the
 * visitor, and items sold elsewhere, which open the merchant's store. Every
 * field but the items has a default, so a payload from before one existed
 * still parses.
 */
export const artistSiteMerchSectionSchema = z.object({
  id: z.string().min(1),
  type: z.literal("merch"),
  title: z.string().max(120).nullable().default(null),
  ...artistSiteSectionLookShape,
  /** A grid of cards, one row that scrolls sideways, or stacked rows. */
  layout: z.enum(["grid", "carousel", "list"]).default("grid"),
  /** The cards side by side on a wide screen. */
  columns: artistSiteGridColumnsSchema.default(4),
  /** A carousel that moves to its next card by itself. */
  autoplay: z.boolean().default(false),
  /**
   * A product on the site's light card, or `plain`: its picture and words
   * straight on the page's background.
   */
  cardStyle: z.enum(["card", "plain"]).default("card"),
  /** The words on every product's button. */
  buttonLabel: z.string().min(1).max(40).default("Buy"),
  /** On sale through 1-tap, or sold elsewhere, in the section's order. */
  items: z.array(artistSiteMerchItemSchema),
  /** What fans in each region see instead of `items` (All markets),
   *  including an empty list where nothing is offered. Optional for
   *  responses from older servers. */
  regions: z.partialRecord(merchTerritorySchema, z.array(artistSiteMerchItemSchema)).optional(),
  /** North America's of `regions`, for site bundles older than the
   *  regions, which refuse a key they do not know. */
  territories: z.partialRecord(legacyMerchTerritorySchema, z.array(artistSiteMerchItemSchema)).optional(),
});

/**
 * The streaming services an artist's profile can be on, each with its icon.
 * The wire takes any service name in `service`, so a page older than a
 * service draws it with a plain icon instead of failing the site.
 */
export const artistSiteStreamingServices = [
  "spotify",
  "appleMusic",
  "youtubeMusic",
  "youtube",
  "amazonMusic",
  "deezer",
  "tidal",
  "soundcloud",
  "bandcamp",
  "audiomack",
  "boomplay",
  "shazam",
] as const;
export type ArtistSiteStreamingService =
  (typeof artistSiteStreamingServices)[number];

/**
 * A Listen on section: a row of buttons, one per streaming profile of the
 * artist (their own page on a streaming service). Every field but the items
 * has a default.
 */
export const artistSiteStreamingProfilesSectionSchema = z.object({
  id: z.string().min(1),
  type: z.literal("streamingProfiles"),
  title: z.string().max(120).nullable().default(null),
  ...artistSiteSectionLookShape,
  /** Each service's icon and name, or its icon alone. */
  display: z.enum(["buttons", "icons"]).default("buttons"),
  items: z.array(
    z.object({
      id: z.string().min(1),
      service: z.string().min(1).max(40),
      /** The service's name, e.g. `Apple Music`. */
      label: z.string().min(1).max(40),
      url: httpUrlSchema,
    }),
  ),
});

/**
 * A contact form: a visitor writes to the artist, and Lissen tells the
 * artist's managers. The email and the message always show; `fields` says
 * which of the others do. Every word is resolved, so the page draws it as
 * served.
 */
export const artistSiteContactSectionSchema = z.object({
  id: z.string().min(1),
  type: z.literal("contact"),
  title: z.string().max(120).nullable().default(null),
  ...artistSiteSectionLookShape,
  description: z.string().max(400).nullable().default(null),
  fields: z
    .object({
      name: z.boolean(),
      subject: z.boolean(),
      phone: z.boolean(),
    })
    .default({ name: true, subject: false, phone: false }),
  labels: z
    .object({
      name: z.string().min(1).max(40),
      email: z.string().min(1).max(40),
      subject: z.string().min(1).max(40),
      phone: z.string().min(1).max(40),
      message: z.string().min(1).max(40),
    })
    .default({
      name: "Name",
      email: "Email",
      subject: "Subject",
      phone: "Phone",
      message: "Message",
    }),
  messagePlaceholder: z.string().max(120).nullable().default(null),
  buttonLabel: z.string().min(1).max(40).default("Send"),
  successMessage: z
    .string()
    .min(1)
    .max(160)
    .default("Thank you. Your message has been sent."),
});

/** Action-token context for a contact message on one artist's site. */
export const contactIdentityContext = (artistSlug: string) =>
  `contact:${artistSlug.trim().toLowerCase()}`;

/**
 * A visitor's message from a site's contact form. The sender is a typed
 * address or an identity action token (purpose `contact`), from which the
 * platform takes the address. The name, subject and phone are sent when the
 * form shows them, and may be left blank.
 */
export const artistContactMessageSchema = z
  .object({
    email: z.string().trim().email().max(254).optional(),
    identityToken: z.string().min(1).max(4096).optional(),
    name: z.string().trim().max(120).optional(),
    subject: z.string().trim().max(200).optional(),
    phone: z.string().trim().max(40).optional(),
    message: z.string().trim().min(1).max(5_000),
    // Honeypot: real visitors never see or populate this field.
    website: z.string().max(0).optional(),
  })
  .refine((request) => !!request.email || !!request.identityToken, {
    message: "email or identityToken is required",
    path: ["email"],
  });

export const artistContactMessageResponseSchema = z.object({
  success: z.literal(true),
});

/**
 * A section of the artist's published Link in Bio page shown on the site,
 * resolved exactly as that page resolves it. The site's own mailing list
 * sign-up is served in this form too, as a `mailingList` section.
 */
export const artistSiteLinkSectionSchema = z.object({
  id: z.string().min(1),
  type: z.literal("linkSection"),
  section: z.union([
    artistLinkPageSectionSchema,
    // Site-owned sign-ups retain a call to action's full copy when swapped.
    artistLinkPageMailingListSectionSchema.extend({
      data: artistLinkPageMailingListSectionSchema.shape.data.extend({
        title: artistSiteCtaSectionSchema.shape.title,
        description: z.string().max(400),
      }),
    }),
  ]),
});

export const artistSiteSectionSchema = z.discriminatedUnion("type", [
  artistSiteHeroSectionSchema,
  artistSiteTextSectionSchema,
  artistSiteColumnsSectionSchema,
  artistSiteVideoSectionSchema,
  artistSiteCarouselSectionSchema,
  artistSiteGallerySectionSchema,
  artistSiteCtaSectionSchema,
  artistSiteReleasesSectionSchema,
  artistSiteDatesSectionSchema,
  artistSiteMerchSectionSchema,
  artistSiteStreamingProfilesSectionSchema,
  artistSiteContactSectionSchema,
  artistSiteLinkSectionSchema,
]);

export const artistSitePageSchema = z.object({
  id: z.string().min(1),
  slug: z.string().min(1).max(60),
  title: z.string().min(1).max(60),
  showInNav: z.boolean(),
  /**
   * The page's own background, in place of the site's; null follows the
   * site. `textColor` replaces the page's text; null reads automatically on
   * its own colour, or follows the site's text when it has no colour.
   */
  background: z
    .object({
      color: colourSchema.nullable(),
      imageUrl: httpUrlSchema.nullable(),
      textColor: colourSchema.nullable().default(null),
    })
    .nullable()
    .default(null),
  sections: z.array(artistSiteSectionSchema),
});

/**
 * Public wire payload of a published artist site: its pages in navigation
 * order, the first being the home page.
 */
export const artistSiteSchema = artistSiteFrameSchema.extend({
  /** The artist's Link in Bio slug, which also addresses the site preview. */
  slug: artistLinkPageSlugSchema,
  /** The custom hostname the site is meant for; null when none is set. */
  hostname: z.string().nullable(),
  artist: artistLinkPageArtistSchema,
  pages: z.array(artistSitePageSchema).min(1),
});

const KNOWN_SECTION_TYPES: ReadonlySet<unknown> = new Set(
  artistSiteSectionSchema.options.map((option) => option.shape.type.value),
);

const KNOWN_COLUMN_BLOCK_TYPES: ReadonlySet<unknown> = new Set(
  artistSiteColumnBlockSchema.options.map((option) => option.shape.type.value),
);

const knownType = (known: ReadonlySet<unknown>) => (value: unknown) =>
  typeof value !== "object" ||
  value === null ||
  known.has((value as { type?: unknown }).type);

/** A Columns section without the blocks whose type this build does not know. */
function withoutUnknownColumnBlocks(section: unknown): unknown {
  if (typeof section !== "object" || section === null) return section;
  const { type, columns } = section as { type?: unknown; columns?: unknown };
  if (type !== "columns" || !Array.isArray(columns)) return section;
  return {
    ...section,
    columns: columns.map((column: unknown) => {
      if (typeof column !== "object" || column === null) return column;
      const { blocks } = column as { blocks?: unknown };
      return Array.isArray(blocks)
        ? {
            ...column,
            blocks: blocks.filter(knownType(KNOWN_COLUMN_BLOCK_TYPES)),
          }
        : column;
    }),
  };
}

/**
 * A site payload without the sections whose type this build does not know,
 * and without the Columns blocks it does not know. A reader calls it before
 * parsing, so a service that is ahead of it (a new section or block type)
 * costs that section or block and not the whole site. Anything that is not
 * shaped like a site is returned as it came, for the schema to refuse.
 */
export function withoutUnknownSiteSections(payload: unknown): unknown {
  if (typeof payload !== "object" || payload === null) return payload;
  const { pages } = payload as { pages?: unknown };
  if (!Array.isArray(pages)) return payload;
  return {
    ...payload,
    pages: pages.map((page: unknown) => {
      if (typeof page !== "object" || page === null) return page;
      const { sections } = page as { sections?: unknown };
      if (!Array.isArray(sections)) return page;
      return {
        ...page,
        sections: sections
          .filter(knownType(KNOWN_SECTION_TYPES))
          .map(withoutUnknownColumnBlocks),
      };
    }),
  };
}

export const artistSiteNotFoundSchema = z.object({
  error: z.literal("artist_site_not_found"),
  slug: z.string(),
});

export type ArtistSite = z.infer<typeof artistSiteSchema>;
export type ArtistSiteFrame = z.infer<typeof artistSiteFrameSchema>;
export type ArtistSiteTheme = z.infer<typeof artistSiteThemeSchema>;
export type ArtistSiteFont = z.infer<typeof artistSiteFontSchema>;
export type ArtistSiteLink = z.infer<typeof artistSiteLinkSchema>;
export type ArtistSiteSocialLink = z.infer<typeof artistSiteSocialLinkSchema>;
export type ArtistSitePage = z.infer<typeof artistSitePageSchema>;
export type ArtistSiteSection = z.infer<typeof artistSiteSectionSchema>;
export type ArtistSiteMerchItem = z.infer<typeof artistSiteMerchItemSchema>;
export type ArtistSiteExternalMerch = z.infer<
  typeof artistSiteExternalMerchSchema
>;
export type ArtistSiteExternalMerchOption = z.infer<
  typeof artistSiteExternalMerchOptionSchema
>;
export type ArtistSiteDatesSection = z.infer<
  typeof artistSiteDatesSectionSchema
>;
export type ArtistSiteMerchSection = z.infer<
  typeof artistSiteMerchSectionSchema
>;
export type ArtistSiteColumnsSection = z.infer<
  typeof artistSiteColumnsSectionSchema
>;
export type ArtistSiteColumnBlock = z.infer<typeof artistSiteColumnBlockSchema>;
export type ArtistSiteVideoSection = z.infer<
  typeof artistSiteVideoSectionSchema
>;
export type ArtistSiteLinkSection = z.infer<typeof artistSiteLinkSectionSchema>;
export type ArtistSiteHeroButton = z.infer<typeof artistSiteHeroButtonSchema>;
export type ArtistSiteStreamingProfilesSection = z.infer<
  typeof artistSiteStreamingProfilesSectionSchema
>;
export type ArtistSiteContactSection = z.infer<
  typeof artistSiteContactSectionSchema
>;
export type ArtistContactMessage = z.infer<typeof artistContactMessageSchema>;
export type ArtistSiteNotFound = z.infer<typeof artistSiteNotFoundSchema>;
